'use client'

import Link from 'next/link'

// Row 8 — Persistent advance affordance (§11.5 LOCKED). Bottom-fixed on
// mobile (thumb zone, ≥44×44px), inline at the column foot on desktop.
// Vermilion fill — the route's single at-rest vermilion placement (§7
// budget 1/2). Label is sequence-aware; the orchestrator above passes the
// resolved label + href.
//
// `kind` differentiates a Link (in-app navigation: next kanji, batch test,
// lesson tail) from a button (within-frame reveal-step advance). The visual
// is identical so the user reads one persistent affordance regardless of
// whether the next tap reveals or routes.
interface Common {
    label: string
    disabled?: boolean
    // Sanctioned mid-lesson exit (Learn batching, spec §11.10): a quiet
    // link under the button naming that leaving is safe. Each frame's
    // draw resolve writes a session-history row, so pausing costs at
    // most the current frame plus the in-flight batch's recall.
    pauseHref?: string
}
type Props =
    | (Common & { kind: 'link'; href: string; onPress?: never })
    | (Common & { kind: 'button'; onPress: () => void; href?: never })

export function AdvanceAffordance(props: Props) {
    const arrow =
        props.label.includes('→') || props.label === 'Continue' ? null : '→'

    const inner = (
        <>
            <span>{props.label}</span>
            {arrow ? (
                <span aria-hidden className="ml-1.5">
                    {arrow}
                </span>
            ) : null}
        </>
    )

    const baseClass =
        'bg-action text-action-on hover:bg-action-hover active:bg-action-pressed outline-focus-ring inline-flex h-12 w-full max-w-xs items-center justify-center rounded-sm font-serif text-base font-medium tracking-wide transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none motion-reduce:active:translate-y-0'

    return (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-rule-soft bg-paper/95 bg-linear-to-t from-paper to-transparent px-5 pt-4 pb-safe backdrop-blur-sm md:px-8">
            <div className="mx-auto flex w-full max-w-2xl justify-center">
                {props.kind === 'link' ? (
                    <Link
                        href={props.href}
                        aria-label={props.label}
                        className={baseClass}
                    >
                        {inner}
                    </Link>
                ) : (
                    <button
                        type="button"
                        onClick={props.onPress}
                        disabled={props.disabled}
                        aria-label={props.label}
                        className={baseClass}
                    >
                        {inner}
                    </button>
                )}
            </div>
            {props.pauseHref ? (
                <div className="mx-auto mt-2 flex w-full max-w-2xl justify-center">
                    <Link
                        href={props.pauseHref}
                        className="font-serif text-[12px] text-muted-fg italic transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        pause here — your progress is kept
                    </Link>
                </div>
            ) : null}
        </div>
    )
}
